import React, { useState } from 'react';
import { trackCTA } from '../../service/usercta';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    useTheme,
    useMediaQuery,
    Card,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlashOnIcon from '@mui/icons-material/FlashOn';

export const allQuickActions = [
    {
        key: "breathe",
        title: "🧘 Breathing Exercise",
        icon: "🧘",
        color: "#22c55e",
        instructionSets: [
            [
                "Find a comfortable seated position",
                "Close your eyes or soften your gaze",
                "Inhale slowly through your nose for 4 counts",
                "Hold your breath for 4 counts",
                "Exhale slowly through your mouth for 6 counts",
                "Repeat this cycle 5-10 times",
                "Notice how your body feels more relaxed"
            ],
            [
                "Sit comfortably with your back supported.",
                "Close your eyes and relax your shoulders.",
                "Inhale slowly through your nose for 4 counts.",
                "Let your belly rise as you breathe in.",
                "Exhale gently through your mouth for 5 counts.",
                "Keep the flow calm and steady.",
                "Repeat 5–10 times, feeling more relaxed."
            ],
            [
                "Sit or lie down in a quiet place.",
                "Inhale gently through your nose.",
                "As you exhale, silently count 'one.'",
                "Next exhale, count 'two.'",
                "Continue until 'five,' then start again.",
                "Keep breathing naturally without force.",
                "Repeat for a few minutes to stay focused."
            ],
            [
                "Place one hand on your chest and one on your belly.",
                "Inhale slowly through your nose for 4 counts.",
                "Feel your belly rise under your hand.",
                "Exhale gently through your mouth for 6 counts.",
                "Notice the chest staying mostly still.",
                "Keep your breath soft and steady.",
                "Repeat for 5 minutes to relax deeply."
            ],
            [
                "Sit comfortably with a straight spine.",
                "Inhale slowly through your nose for 5 counts.",
                "Exhale slowly through your nose for 5 counts.",
                "Keep the rhythm smooth and equal.",
                "Focus only on the breath coming and going.",
                "Relax your body with each cycle.",
                "Continue for 10 rounds."
            ],
            [
                "Sit back and drop your shoulders.",
                "Inhale deeply through your nose.",
                "Hold your breath for just a moment.",
                "Exhale with a gentle sigh through your mouth.",
                "Feel tension leaving your body.",
                "Smile softly as you release stress.",
                "Repeat 5–7 times."
            ],
            [
                "Sit upright with open chest.",
                "Inhale deeply through your nose for 3 counts.",
                "Exhale quickly through your mouth with a 'whoosh.'",
                "Repeat with steady rhythm.",
                "Keep breaths short but alert.",
                "Notice your energy rising.",
                "Continue for 1–2 minutes."
            ],
            [
                "Lie down or sit in a calm space.",
                "Inhale slowly through your nose for 4 counts.",
                "Hold your breath gently for 2 counts.",
                "Exhale slowly through your mouth for 6 counts.",
                "Relax your jaw and shoulders.",
                "Let your body feel heavier with each breath.",
                "Continue until you feel calm."
            ],
            [
                "Sit comfortably with eyes closed.",
                "Inhale halfway, pause.",
                "Inhale fully, filling your lungs.",
                "Exhale halfway, pause.",
                "Exhale fully, emptying your lungs.",
                "Keep the flow natural and smooth.",
                "Repeat 5–8 times to reset."
            ],
            [
                "Stop what you’re doing and sit still.",
                "Take a slow breath in through your nose.",
                "Exhale gently through your mouth.",
                "Notice your shoulders dropping.",
                "Take another slow breath in.",
                "Release it gently.",
                "Repeat 3–5 times anytime you need a pause."
            ],
            [
                "Sit comfortably with your eyes closed.",
                "Inhale gently through your nose for 4 counts.",
                "As you exhale, relax your face.",
                "Inhale again, imagine breathing in calm.",
                "Exhale with a soft smile.",
                "Keep your body loose and light.",
                "Repeat for a few minutes to uplift mood."
            ]
        ]
    },
    {
        key: "stretch",
        title: "💪 Desk Stretches",
        icon: "💪",
        color: "#3b82f6",
        instructionSets: [
            [
                "Stand up from your chair",
                "Roll your shoulders backward 5 times",
                "Gently turn your head left and right",
                "Reach your arms up toward the ceiling",
                "Touch your toes or reach toward the floor",
                "Stretch your arms across your chest",
                "Take a deep breath and feel refreshed"
            ],
            [
                "Stand up from your chair",
                "Roll your shoulders backward 5 times",
                "Roll your shoulders forward 5 times",
                "Gently turn your head left and right",
                "Reach your arms up toward the ceiling",
                "Touch your toes or reach toward the floor",
                "Take a deep breath and sit back refreshed"
            ],
            [
                "Sit tall at the edge of your chair",
                "Drop your right ear toward your right shoulder",
                "Hold for a few seconds, then switch sides",
                "Roll your neck slowly in a half-circle",
                "Shrug your shoulders up and drop them down",
                "Place your right hand across to your left shoulder and stretch gently",
                "Relax and breathe deeply"
            ],
            [
                "Sit tall and clasp your hands in front of you",
                "Push your palms forward",
                "Round your upper back as you stretch forward",
                "Drop your chin slightly toward your chest",
                "Hold for a few seconds and breathe",
                "Release slowly and bring arms down",
                "Repeat 2–3 times"
            ],
            [
                "Sit tall with both feet on the floor",
                "Place your left hand on your chair seat",
                "Reach your right arm up toward the ceiling",
                "Gently lean to the left",
                "Feel the stretch along your side",
                "Switch to the other side",
                "Take a deep breath and relax"
            ],
            [
                "Stand behind your chair",
                "Clasp your hands behind your back",
                "Straighten your arms gently",
                "Lift your chest and look upward",
                "Hold for a few breaths",
                "Release slowly and shake arms out",
                "Sit back down feeling open and refreshed"
            ],
            [
                "Sit tall with both feet flat on the floor",
                "Place your right hand on the back of your chair",
                "Place your left hand on your right thigh",
                "Inhale and lengthen your spine",
                "Exhale and twist gently to the right",
                "Hold for a few seconds, then switch sides",
                "Return to center and relax"
            ],
            [
                "Stand up behind your chair",
                "Place your right heel on the edge of the chair",
                "Keep your leg straight",
                "Inhale and reach your arms toward your toes",
                "Hold the stretch for a few seconds",
                "Switch to the other leg",
                "Relax and sit back down"
            ],
            [
                "Sit tall at your desk",
                "Extend your right arm forward, palm up",
                "With your left hand, gently pull back your fingers",
                "Hold for a few seconds",
                "Switch to palm down, pull fingers downward",
                "Repeat with the other arm",
                "Shake your hands out to release tension"
            ],
            [
                "Stand up from your chair",
                "Place your hands on your desk for support",
                "Step your right leg back slightly",
                "Press your heel toward the floor",
                "Hold for a few seconds, feeling the calf stretch",
                "Switch legs",
                "Take a deep breath and sit down relaxed"
            ],
            [
                "Sit comfortably with feet on the floor",
                "Inhale and raise your arms overhead",
                "Exhale and fold forward toward the floor",
                "Let your head and arms hang loosely",
                "Slowly roll up to sitting",
                "Stretch arms wide open",
                "Take one deep breath and smile"
            ]
        ]
    },
    {
        key: "gratitude",
        title: "🙏 Gratitude Moment",
        icon: "🙏",
        color: "#8b5cf6",
        instructionSets: [
            [
                "Pause what you're doing for a moment",
                "Think of one thing you're grateful for today",
                "Consider why this thing matters to you",
                "Feel the positive emotion in your body",
                "Think of a person you appreciate",
                "Send them a mental 'thank you'",
                "Carry this feeling with you"
            ],
            [
                "Sit quietly with eyes closed",
                "Place your hand on your heart",
                "Take a deep breath in and out",
                "Think of one person you’re grateful for",
                "Picture them smiling in your mind",
                "Silently say, 'Thank you'",
                "Carry that feeling into your day"
            ],
            [
                "Sit comfortably in your chair",
                "Inhale slowly and relax your shoulders",
                "Thank your feet for carrying you today",
                "Thank your hands for helping you work",
                "Thank your mind for learning and creating",
                "Take a deep breath of appreciation",
                "Smile gently at yourself"
            ],
            [
                "Stop what you’re doing",
                "Place both hands on your lap",
                "Take 3 slow breaths",
                "Think of something that made you smile today",
                "Feel that moment again in your heart",
                "Whisper 'thank you' softly",
                "Return to your work refreshed"
            ],
            [
                "Sit in a calm place",
                "Close your eyes and take a deep breath",
                "Recall one good thing that happened today",
                "Recall a second small blessing",
                "Recall a third simple joy",
                "Smile and say 'thank you' for each one",
                "End the day with peace"
            ],
            [
                "Look out the window or step outside",
                "Take a deep breath of fresh air",
                "Notice the sky, clouds, or sunshine",
                "Thank the trees for giving shade and oxygen",
                "Thank the birds or sounds of life around you",
                "Smile at the beauty of nature",
                "Feel refreshed and connected"
            ],
            [
                "Sit with your back straight",
                "Close your eyes and take 3 deep breaths",
                "Think of a challenge you are facing",
                "Ask yourself, 'What can I learn from this?'",
                "Thank the challenge for teaching you",
                "Release tension with a long exhale",
                "Open your eyes with new strength"
            ],
            [
                "Take a notebook and pen",
                "Write down 3 things you are grateful for",
                "Pause and breathe after each one",
                "Imagine each word glowing on the page",
                "Read your list out loud or silently",
                "Smile at the goodness you’ve recorded",
                "Close the book with thanks"
            ],
            [
                "Think of a friend who supports you",
                "Close your eyes and picture their face",
                "Inhale deeply and smile softly",
                "Recall a happy memory with them",
                "Whisper 'thank you' for their presence",
                "Send them good wishes in your mind",
                "Feel the warmth of connection"
            ],
            [
                "Sit at your desk and pause for a moment",
                "Inhale deeply, then exhale slowly",
                "Think of one tool or resource that helps you daily",
                "Think of one colleague or supporter who makes work easier",
                "Appreciate the opportunity to learn and grow",
                "Whisper a quiet 'thank you'",
                "Return to your work with positivity"
            ],
            [
                "Sit comfortably and close your eyes",
                "Inhale slowly through your nose",
                "As you inhale, think 'I receive'",
                "Exhale gently through your mouth",
                "As you exhale, think 'I give thanks'",
                "Repeat for 5–10 breaths",
                "End with a soft smile"
            ]
        ]
    },
    {
        key: "smile",
        title: "😊 Smile Break",
        icon: "😊",
        color: "#ef4444",
        instructionSets: [
            [
                "Put down what you're doing",
                "Take a deep breath in",
                "Smile genuinely, even if you don't feel like it",
                "Hold the smile for 10 seconds",
                "Think of something that makes you happy",
                "Let the smile reach your eyes",
                "Notice how your mood has shifted"
            ],
            [
                "Sit comfortably in your chair",
                "Close your eyes and take a deep breath",
                "Exhale slowly while forming a gentle smile",
                "Relax your shoulders and jaw",
                "Feel the calm spreading through your body",
                "Take another slow breath with the smile",
                "Open your eyes and continue your day with ease"
            ],
            [
                "Close your eyes and sit quietly",
                "Take a deep breath in",
                "Recall a happy memory from your life",
                "As you picture it, let a smile form naturally",
                "Hold that memory for a few breaths",
                "Whisper 'thank you' to yourself",
                "Open your eyes still smiling"
            ],
            [
                "Sit upright and relax your body",
                "Inhale slowly through your nose",
                "Think of one thing you are grateful for",
                "Exhale gently with a smile",
                "Feel gratitude warming your heart",
                "Repeat for 3 breaths",
                "Return to your work smiling softly"
            ],
            [
                "Find a mirror nearby or imagine one",
                "Look at your reflection or visualize yourself",
                "Inhale deeply, then smile at yourself",
                "Notice how your face softens",
                "Say silently, 'You are doing well'",
                "Take one more breath with the smile",
                "Return to your tasks uplifted"
            ],
            [
                "Sit with your hand on your chest",
                "Inhale deeply and close your eyes",
                "Picture a warm light inside your heart",
                "Exhale and let that light spread with a smile",
                "Feel kindness filling your chest",
                "Breathe in again with the smile",
                "Carry this warmth into your day"
            ],
            [
                "Pause your work for a moment",
                "Sit back and relax your arms",
                "Take a deep breath and smile softly",
                "Think of one good thing about your work today",
                "Exhale slowly, keeping the smile",
                "Whisper 'thank you' for the opportunity",
                "Return to work refreshed"
            ],
            [
                "Look outside your window or imagine nature",
                "Inhale deeply, noticing the air",
                "Picture trees, sky, or sunshine",
                "Exhale with a gentle smile",
                "Appreciate the beauty around you",
                "Breathe again, smiling at nature’s gifts",
                "Return to your tasks feeling connected"
            ],
            [
                "Sit quietly with closed eyes",
                "Think of someone who was kind to you",
                "Inhale slowly and smile at the thought",
                "Send them silent thanks in your mind",
                "Exhale with warmth and joy",
                "Repeat for 2–3 breaths",
                "Open your eyes with a soft smile"
            ],
            [
                "Sit upright and take a deep breath",
                "Force a wide smile for 5 seconds",
                "Exhale and relax your face",
                "Do it again, adding a small chuckle",
                "Let yourself laugh lightly",
                "Feel the release of tension",
                "Return to calm smiling softly"
            ],
            [
                "Sit comfortably with your eyes closed",
                "Take a deep, slow breath",
                "Think of 3 good things that happened today",
                "Smile as you recall each one",
                "Whisper 'thank you' after each thought",
                "Exhale slowly with the smile",
                "End your day feeling light and positive"
            ]
        ]
    }
];

// Helper hook to get user data
const useUserData = () => {
    const storedUser = localStorage.getItem("user");
    const user = storedUser ? JSON.parse(storedUser) : null;
    return {
        name: user?.name || "Guest",
        packageType: user?.package_type || 'basic' // Default to basic if not set
    };
};

// Function to pick a random set
const getRandomSet = (action) => {
    const sets = action.instructionSets;
    const randomIndex = Math.floor(Math.random() * sets.length);
    return sets[randomIndex];
};

// Main QuickActions component
export const QuickActionsList = () => {
    const { packageType } = useUserData();
    const [selectedAction, setSelectedAction] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Select quick actions based on package type
    const quickActions = (() => {
        const shuffled = [...allQuickActions].sort(() => 0.5 - Math.random());
        const actionCount = packageType === 'advanced' ? 5 : 3;
        return shuffled.slice(0, Math.min(actionCount, allQuickActions.length));
    })();

    const handleActionClick = (action) => {
        const randomInstructions = getRandomSet(action);
        setSelectedAction({ ...action, instructions: randomInstructions });
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setSelectedAction(null);
    };
    return (
        <Card sx={{
            p: 2,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
            border: '1px solid #bbf7d0',
            boxShadow: '0 2px 8px rgba(34, 197, 94, 0.08)'
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5
                }}>
                    <FlashOnIcon sx={{ color: 'white', fontSize: 16 }} />
                </Box>
                <Typography variant="subtitle2" sx={{ color: '#166534', fontWeight: 600, fontSize: '0.9rem' }}>
                    Quick Actions
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {quickActions.map((action) => (
                    <Button
                        key={action.key}
                        size="small"
                        variant="outlined"
                        onClick={() => handleActionClick(action)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            py: 0.6,
                            px: 1.25,
                            borderRadius: 1.5,
                            // color: '#fff',
                            // borderColor: '#22c55e',
                            background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
                            // '&:hover': {
                            //     backgroundColor: '#f0fdf4',
                            //     borderColor: '#16a34a'
                            // }
                        }}
                    >
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <span style={{ fontSize: '1rem' }}>{action.icon}</span>
                            <span>{action.title.split(' ').slice(1).join(' ')}</span>
                        </Box>
                    </Button>
                ))}
            </Box>

            {selectedAction && (
                <QuickActionModal
                    open={isModalOpen}
                    onClose={handleClose}
                    action={selectedAction}
                />
            )}
        </Card>
    );
};

// Quick Action Modal Component
const QuickActionModal = ({ open, onClose, action }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!action) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    [theme.breakpoints.down('sm')]: {
                        m: 1,
                        borderRadius: 2
                    }
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
                pb: 1.5,
                backgroundColor: action.color,
                color: 'white'
            }}>
                <Typography variant="h6" fontWeight={600}>
                    {action.icon} {action.title}
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ color: 'white' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ py: 3 }}>
                <List>
                    {action.instructions.map((step, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <Box sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    backgroundColor: `${action.color}20`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: action.color,
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem'
                                }}>
                                    {index + 1}
                                </Box>
                            </ListItemIcon>
                            <ListItemText
                                primary={step}
                                primaryTypographyProps={{
                                    variant: 'body1',
                                    color: 'text.primary'
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                                    onClick={() => {
                // 🔥 Track CTA event
                const storedUser = localStorage.getItem('user');
                const user = storedUser ? JSON.parse(storedUser) : null;
                
                if (user && user.id) {
                    trackCTA(action.key, user, window.location.pathname);
                    console.log(`✅ CTA tracked: ${action.key}`);
                }
                
                onClose();
            }}
                        sx={{
                            backgroundColor: action.color,
                            '&:hover': {
                                backgroundColor: `${action.color}E6`,
                            },
                            textTransform: 'none',
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            fontWeight: 600
                        }}
                    >
                        Got it, thanks!
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default QuickActionsList;
